import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { FadeUp } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  "Fill in the form below with the email address you use in the app.",
  "We instantly receive your “Delete my account” request.",
  "Your account and all associated data are permanently deleted within 30 days — and we confirm by email when it's done.",
];

const DELETED = [
  "Your pantry items and their expiry dates",
  "Your events and shopping lists",
  "Your reminders and notification preferences",
  "Your account itself",
];

export default function DeleteAccount() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus("loading");
    try {
      await axios.post(`${API}/delete-request`, { email });
      setStatus("done");
      toast.success("Request received — we'll confirm by email once it's done.");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send your request. Try again in a moment.");
    }
  };

  return (
    <div className="min-h-screen px-6 md:px-12 py-10 md:py-14" data-testid="delete-account-page">
      <div className="max-w-[1100px] mx-auto">
        <Link
          to="/"
          data-testid="delete-account-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#111111]/50 hover:text-[#16783F] transition-colors duration-300"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to site
        </Link>

        <header className="mt-12 border-b border-[#111111]/10 pb-12">
          <div className="flex items-center gap-3">
            <Logo testid="delete-account-logo" className="h-10 w-10" />
            <span className="font-serif text-2xl tracking-tight">My Fresh Tracker</span>
          </div>
          <h1
            className="font-serif font-light tracking-tight text-5xl md:text-7xl mt-10"
            data-testid="delete-account-title"
          >
            Account <em className="text-[#1F9D55]">Deletion</em>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#111111]/70 max-w-xl leading-relaxed" data-testid="delete-account-intro">
            You can request to have your account and all associated data deleted at any time by filling out the
            form below. No calls, no hoops — one email address is all we need.
          </p>
        </header>

        <div className="mt-16 pb-24 grid md:grid-cols-12 gap-16">
          <div className="md:col-span-6">
            <FadeUp>
              <p className="uppercase tracking-[0.2em] text-xs font-semibold text-[#16783F]">How it works</p>
              <div className="mt-8 space-y-8">
                {STEPS.map((step, i) => (
                  <div key={i} className="flex gap-5" data-testid={`delete-step-${i + 1}`}>
                    <span className="font-mono text-sm text-[#16783F] mt-1">{String(i + 1).padStart(2, "0")}</span>
                    <p className="text-base md:text-lg leading-relaxed text-[#111111]/75">{step}</p>
                  </div>
                ))}
              </div>
              <p className="mt-14 uppercase tracking-[0.2em] text-xs font-semibold text-[#16783F]">
                What gets deleted
              </p>
              <ul className="mt-6 space-y-3">
                {DELETED.map((item) => (
                  <li key={item} className="flex items-baseline gap-3 text-base text-[#111111]/70">
                    <span className="text-[#1F9D55]">—</span>
                    {item}
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>

          <div className="md:col-span-6">
            <FadeUp delay={0.15}>
              <form
                onSubmit={submit}
                className="bg-[#FFFFFF] border border-[#111111]/10 border-l-4 border-l-[#1F9D55] p-8 md:p-10"
                data-testid="delete-account-form"
              >
                <h2 className="font-serif text-3xl tracking-tight">Request deletion</h2>
                <p className="mt-4 text-sm text-[#111111]/60 leading-relaxed">
                  Enter the email address linked to your My Fresh Tracker account. We&rsquo;ll receive a
                  &ldquo;Delete my account&rdquo; request immediately and take it from there.
                </p>
                <div className="mt-8">
                  <label
                    htmlFor="delete-email"
                    className="uppercase tracking-[0.2em] text-xs font-semibold text-[#111111]/50"
                  >
                    Email used in the app
                  </label>
                  <input
                    id="delete-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="delete-email-input"
                    className="mt-2 w-full bg-transparent outline-none border-b border-[#111111]/25 focus:border-[#1F9D55] transition-colors duration-300 py-4 text-lg placeholder:text-[#111111]/35"
                  />
                </div>
                <button
                  type="submit"
                  disabled={status === "loading"}
                  data-testid="delete-submit-button"
                  className="mt-8 rounded-full bg-[#16783F] text-[#F7F6F2] px-8 py-4 text-xs uppercase tracking-[0.2em] font-semibold inline-flex items-center gap-2 hover:bg-[#111111] transition-colors duration-300 disabled:opacity-60"
                >
                  {status === "loading" ? "Sending" : "Delete my account"}
                  <ArrowRight className="h-3.5 w-3.5" />
                </button>
                {status === "done" && (
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="mt-6 text-sm font-medium text-[#16783F]"
                    data-testid="delete-success-message"
                  >
                    Request received. We&rsquo;ll email you once your account and data are deleted.
                  </motion.p>
                )}
              </form>
            </FadeUp>
          </div>
        </div>
      </div>
    </div>
  );
}
